import React, { useState } from 'react';
import { platesApi } from './api';
import { CANCEL_REASONS, REJECT_REASONS } from './platesConfig';
import ModalShell from '../insurance/ModalShell';

export default function AdminActionsModal({ visible, onHide, action, qpId, plate, view, toast, onDone, onRequestCorrection }) {
  if (!visible || !action) return null;

  if (action === 'modify') return <ModifyForm visible={visible} onHide={onHide} plate={plate} toast={toast} onDone={onDone} />;
  if (action === 'cancel') return <CancelForm visible={visible} onHide={onHide} plate={plate} toast={toast} onDone={onDone} />;
  if (action === 'reject') return <RejectForm visible={visible} onHide={onHide} qpId={qpId} plate={plate} toast={toast} onDone={onDone} />;
  if (action === 'reopen') return <ReopenForm visible={visible} onHide={onHide} plate={plate} toast={toast} onDone={onDone} onRequestCorrection={onRequestCorrection} />;
  return null;
}

function ModifyForm({ visible, onHide, plate, toast, onDone }) {
  const beforeOwnerName = plate?.owner_name || '';
  const beforeOwnerRfc = plate?.owner_rfc || '';
  const beforeGestorName = plate?.gestor_name || '';
  const beforeGestorEmail = plate?.gestor_email || '';

  const [ownerName, setOwnerName] = useState(beforeOwnerName);
  const [ownerRfc, setOwnerRfc] = useState(beforeOwnerRfc);
  const [gestorName, setGestorName] = useState(beforeGestorName);
  const [gestorEmail, setGestorEmail] = useState(beforeGestorEmail);
  const [motivo, setMotivo] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const ownerNameChanged = ownerName !== beforeOwnerName;
  const ownerRfcChanged = ownerRfc !== beforeOwnerRfc;
  const gestorNameChanged = gestorName !== beforeGestorName;
  const gestorEmailChanged = gestorEmail !== beforeGestorEmail;
  const anyChanged = ownerNameChanged || ownerRfcChanged || gestorNameChanged || gestorEmailChanged;
  const canSave = motivo.trim().length > 5 && anyChanged;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setArchivo({ file, nombre: file.name, size: formatBytes(file.size) });
    e.target.value = '';
  };

  const submit = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (ownerNameChanged) fd.append('changes[owner_name]', ownerName);
      if (ownerRfcChanged) fd.append('changes[owner_rfc]', ownerRfc);
      if (gestorNameChanged) fd.append('changes[gestor_name]', gestorName);
      if (gestorEmailChanged) fd.append('changes[gestor_email]', gestorEmail);
      fd.append('reason', motivo);
      if (archivo?.file) fd.append('proof', archivo.file);
      const res = await platesApi.modify(plate.id, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Placa modificada', life: 2500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={720}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Acción administrativa</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>Modificar placa</div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4, fontFamily: 'var(--pei-font-mono)' }}>
          {plate?.plate_number || '—'}
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        <div className="banner info" style={{ marginBottom: 18 }}>
          <i className="pi pi-info-circle" />
          <div>
            <div className="b-title">Solo datos del titular o del gestor</div>
            <div className="b-sub">Para cambiar la placa o las fechas, cancela la actual y crea una nueva.</div>
          </div>
        </div>

        <div className="pei-label" style={{ marginBottom: 10 }}>Datos del titular</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          <div>
            <div className="pei-label">Nombre del titular</div>
            <input
              className="pei-input"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              style={ownerNameChanged ? { borderColor: 'var(--pei-cyan-bright)', background: 'rgba(34,236,154,0.06)' } : undefined}
            />
          </div>
          <div>
            <div className="pei-label">RFC del titular</div>
            <input
              className="pei-input"
              value={ownerRfc}
              onChange={(e) => setOwnerRfc(e.target.value.toUpperCase())}
              style={ownerRfcChanged ? { borderColor: 'var(--pei-cyan-bright)', background: 'rgba(34,236,154,0.06)' } : undefined}
            />
          </div>
        </div>

        <div className="pei-label" style={{ marginBottom: 10 }}>Datos del gestor</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          <div>
            <div className="pei-label">Nombre del gestor</div>
            <input
              className="pei-input"
              value={gestorName}
              onChange={(e) => setGestorName(e.target.value)}
              style={gestorNameChanged ? { borderColor: 'var(--pei-cyan-bright)', background: 'rgba(34,236,154,0.06)' } : undefined}
            />
          </div>
          <div>
            <div className="pei-label">Correo del gestor</div>
            <input
              className="pei-input"
              type="email"
              value={gestorEmail}
              onChange={(e) => setGestorEmail(e.target.value)}
              style={gestorEmailChanged ? { borderColor: 'var(--pei-cyan-bright)', background: 'rgba(34,236,154,0.06)' } : undefined}
            />
          </div>
        </div>

        <div style={{ marginTop: 4, marginBottom: 18 }}>
          <div className="pei-label">
            Motivo del cambio <span style={{ color: 'var(--pei-danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>*</span>
          </div>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Explica brevemente el cambio (corrección de titular, cambio de gestor…)"
            className="pei-textarea"
            rows={3}
          />
        </div>

        <MiniDropzone label="Adjuntar comprobante (opcional)" archivo={archivo} setArchivo={setArchivo} onPickFile={handleFile} />
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={submitting}>Cancelar</button>
        <div style={{ flex: 1 }} />
        <button
          className="pei-btn pei-btn-primary floor-shadow"
          onClick={submit}
          disabled={!canSave || submitting}
        >
          {submitting ? 'Guardando…' : 'Guardar modificación'}
        </button>
      </div>
    </ModalShell>
  );
}

function CancelForm({ visible, onHide, plate, toast, onDone }) {
  const [motivoTipo, setMotivoTipo] = useState('');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [notas, setNotas] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOtro = motivoTipo === 'otro';
  const motivoFinal = isOtro ? motivoTexto : motivoTipo;
  const canCancel = motivoFinal.length > 0 && confirmText === 'CANCELAR' && !submitting;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setArchivo({ file, nombre: file.name, size: formatBytes(file.size) });
    e.target.value = '';
  };

  const submit = async () => {
    if (!canCancel) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('reason', motivoFinal);
      if (fecha) fd.append('effective_date', fecha);
      if (notas) fd.append('notes', notas);
      if (archivo?.file) fd.append('proof', archivo.file);
      const res = await platesApi.cancel(plate.id, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Placa cancelada', life: 3500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={640}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6, color: 'var(--pei-danger)' }}>Acción destructiva</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>Cancelar placa</div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
          {plate?.gestor_name || 'Gestor de placas'} · <span style={{ fontFamily: 'var(--pei-font-mono)' }}>{plate?.plate_number || '—'}</span>
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        <div className="banner danger" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">La placa quedará marcada como cancelada</div>
            <div className="b-sub">Puedes crear una nueva después. El histórico se conserva.</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">
            Motivo de cancelación <span style={{ color: 'var(--pei-danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>*</span>
          </div>
          <select className="pei-select" value={motivoTipo} onChange={(e) => setMotivoTipo(e.target.value)}>
            <option value="">Selecciona…</option>
            {CANCEL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {isOtro && (
            <input
              autoFocus
              className="pei-input"
              placeholder="Especifica el motivo…"
              value={motivoTexto}
              onChange={(e) => setMotivoTexto(e.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <div className="pei-label">Fecha efectiva</div>
            <input type="date" className="pei-input" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">Notas adicionales (opcional)</div>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="pei-textarea" rows={3}
            placeholder="Cualquier información que ayude a entender el contexto…" />
        </div>

        <MiniDropzone label="Comprobante de cancelación (opcional)" archivo={archivo} setArchivo={setArchivo} onPickFile={handleFile} />

        <div style={{ marginTop: 18, padding: 16, background: 'rgba(220,38,38,0.04)', border: '1.5px solid rgba(220,38,38,0.30)', borderRadius: 12 }}>
          <div style={{ fontSize: 12.5, color: 'var(--pei-text)', fontWeight: 500, marginBottom: 8 }}>
            Para confirmar, escribe <span style={{ fontFamily: 'var(--pei-font-mono)', background: 'white', padding: '1px 8px', borderRadius: 4, fontWeight: 700 }}>CANCELAR</span> en el campo:
          </div>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="CANCELAR"
            style={{
              width: '100%', height: 40, padding: '0 14px',
              border: `1.5px solid ${confirmText === 'CANCELAR' ? 'var(--pei-danger)' : 'rgba(220,38,38,0.30)'}`,
              borderRadius: 10, fontSize: 14, fontFamily: 'var(--pei-font-mono)',
              fontWeight: 700, letterSpacing: '0.08em',
              color: 'var(--pei-danger)',
              background: 'white',
              outline: 'none',
            }}
          />
        </div>
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={submitting}>Volver</button>
        <div style={{ flex: 1 }} />
        <button className="pei-btn pei-btn-danger" disabled={!canCancel} onClick={submit}>
          <i className="pi pi-ban" style={{ fontSize: 12 }} /> {submitting ? 'Cancelando…' : 'Cancelar placa'}
        </button>
      </div>
    </ModalShell>
  );
}

function RejectForm({ visible, onHide, qpId, plate, toast, onDone }) {
  const [motivoTipo, setMotivoTipo] = useState('');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [gestorName, setGestorName] = useState(plate?.gestor_name || '');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOtro = motivoTipo === 'otro';
  const motivoFinal = isOtro ? motivoTexto : motivoTipo;
  const canConfirm = motivoFinal.length > 2 && !submitting;

  const gestorInitials = (gestorName || 'GP')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'GP';

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await platesApi.reject(qpId, { reason: motivoFinal, notes: notas });
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Rechazo registrado', detail: 'El paso queda disponible para reintentar', life: 3500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={640}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Provider rejected</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>
          El gestor rechazó la solicitud
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
          Sin placa emitida. El paso quedará disponible para reintentar.
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">Gestor de placas</div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px',
            border: '1.5px solid var(--pei-gray-5)',
            background: 'var(--pei-bg-readonly)',
            borderRadius: 12,
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8,
              background: 'rgba(50,184,223,0.14)', color: 'var(--pei-cyan)',
              display: 'grid', placeItems: 'center',
              fontSize: 11, fontWeight: 700, flexShrink: 0,
            }}>
              {gestorInitials}
            </div>
            <input
              value={gestorName}
              onChange={(e) => setGestorName(e.target.value)}
              style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: 'var(--pei-text)', outline: 'none' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">
            Motivo del rechazo <span style={{ color: 'var(--pei-danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>*</span>
          </div>
          <select className="pei-select" value={motivoTipo} onChange={(e) => setMotivoTipo(e.target.value)}>
            <option value="">Selecciona…</option>
            {REJECT_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {isOtro && (
            <input autoFocus className="pei-input" placeholder="Especifica el motivo…"
              value={motivoTexto} onChange={(e) => setMotivoTexto(e.target.value)} style={{ marginTop: 8 }} />
          )}
        </div>

        <div>
          <div className="pei-label">Notas</div>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="pei-textarea" rows={3}
            placeholder="Cualquier información adicional para el histórico…" />
        </div>
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={submitting}>Cancelar</button>
        <div style={{ flex: 1 }} />
        <button className="pei-btn pei-btn-primary floor-shadow" disabled={!canConfirm} onClick={submit}>
          {submitting ? 'Confirmando…' : 'Confirmar rechazo'}
        </button>
      </div>
    </ModalShell>
  );
}

function ReopenForm({ visible, onHide, plate, toast, onDone, onRequestCorrection }) {
  const [hover, setHover] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (mode) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await platesApi.reopen(plate.id, { mode });
      if (res?.data?.success) {
        if (mode === 'fix') {
          // Registramos el evento 'reopened' y abrimos el formulario de
          // corrección con los valores precargados (no reenvía correo).
          onRequestCorrection?.();
        } else {
          toast?.current?.show?.({
            severity: 'success',
            summary: 'Placa cancelada, paso disponible',
            life: 3500,
          });
          onDone?.();
          onHide?.();
        }
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={680}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '32px 36px 16px' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Reabrir paso</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.012em', marginBottom: 6 }}>
          ¿Qué tipo de cambio necesitas?
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginBottom: 24 }}>
          Elige según lo que vayas a corregir o reemplazar.
        </div>
      </div>

      <div style={{ padding: '0 36px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ReopenCard
          icon="pi-pencil"
          iconBg="rgba(50,184,223,0.14)"
          iconColor="var(--pei-cyan)"
          title="Corrección tipográfica"
          desc="Editar la misma placa (número de placa, fechas, etc.). Misma row, queda registrado como `corrected`."
          chip="Hoy"
          chipBg="rgba(50,184,223,0.16)"
          chipColor="var(--pei-blue-deeper)"
          hovered={hover === 'fix'}
          onMouseEnter={() => setHover('fix')}
          onMouseLeave={() => setHover(null)}
          onClick={() => submit('fix')}
          disabled={submitting}
        />
        <ReopenCard
          icon="pi-refresh"
          iconBg="rgba(34,236,154,0.18)"
          iconColor="var(--pei-green-true)"
          title="Cambio de placa"
          desc="Cancelar la actual y crear una nueva desde cero. El histórico de la anterior se conserva."
          chip="Crea nueva"
          chipBg="rgba(34,236,154,0.20)"
          chipColor="#0a7c4c"
          hovered={hover === 'replace'}
          onMouseEnter={() => setHover('replace')}
          onMouseLeave={() => setHover(null)}
          onClick={() => submit('replace')}
          disabled={submitting}
        />
      </div>
    </ModalShell>
  );
}

function ReopenCard({ icon, iconBg, iconColor, title, desc, chip, chipBg, chipColor, hovered, disabled, ...rest }) {
  return (
    <button {...rest} disabled={disabled} style={{
      background: hovered ? 'var(--pei-bg-soft)' : 'white',
      border: `1.5px solid ${hovered ? 'var(--pei-cyan-bright)' : 'var(--pei-gray-5)'}`,
      borderRadius: 16,
      padding: '20px 18px',
      textAlign: 'left',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12,
      opacity: disabled ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: iconBg, color: iconColor, display: 'grid', placeItems: 'center' }}>
          <i className={`pi ${icon}`} style={{ fontSize: 18 }} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', background: chipBg, color: chipColor, borderRadius: 999, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {chip}
        </span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--pei-text-helper)', lineHeight: 1.5 }}>{desc}</div>
      <div style={{ fontSize: 12, color: 'var(--pei-cyan)', fontWeight: 600, marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        Continuar <i className="pi pi-arrow-right" style={{ fontSize: 10 }} />
      </div>
    </button>
  );
}

function MiniDropzone({ label, archivo, setArchivo, onPickFile }) {
  const inputRef = React.useRef(null);
  return (
    <div>
      <div className="pei-label">{label}</div>
      <input ref={inputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={onPickFile} />
      {archivo ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px',
          background: 'white',
          border: '1px solid var(--pei-gray-5)',
          borderRadius: 12,
        }}>
          <div style={{ width: 32, height: 40, borderRadius: 4, background: '#fff5f5', border: '1px solid #fecaca', display: 'grid', placeItems: 'center', color: 'var(--pei-danger)', fontSize: 9, fontWeight: 700 }}>PDF</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text)' }}>{archivo.nombre}</div>
            <div style={{ fontSize: 11, color: 'var(--pei-text-helper)' }}>{archivo.size}</div>
          </div>
          <button onClick={() => setArchivo(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--pei-text-helper)' }}>
            <i className="pi pi-times" style={{ fontSize: 13 }} />
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} type="button" style={{
          width: '100%', padding: '14px 16px',
          background: 'transparent',
          border: '1.5px dashed var(--pei-cyan-bright)',
          borderRadius: 12,
          color: 'var(--pei-cyan)', fontSize: 13, fontWeight: 500,
          cursor: 'pointer', fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <i className="pi pi-cloud-upload" style={{ fontSize: 14 }} />
          Arrastra o haz click para adjuntar
        </button>
      )}
    </div>
  );
}

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const k = 1024;
  if (b < k) return b + ' B';
  if (b < k * k) return Math.round(b / k) + ' KB';
  return (b / (k * k)).toFixed(1) + ' MB';
}
